import React, { useState, useEffect } from 'react';
import ErrorBoundary from '../shared/ErrorBoundary';
import SpanAttributesSection from '../shared/SpanAttributesSection';
import JSONTreeViewer from '../../../../components/JSONTreeViewer';
import { extractToolAttributes, extractEmbeddingAttributes, extractRerankerAttributes } from '../../../../utils/openinference';
import { typography, palette, withOpacity } from '../../../../utils/designTokens';
import { logger } from '../../../../utils/logger';

/**
 * AttributesSection - Displays span attributes with Structured/Raw toggle
 */
const AttributesSection = ({ attributes }) => {
  const [viewMode, setViewMode] = useState('structured'); // 'structured' or 'raw'

  let parsedAttrs;
  try {
    parsedAttrs = typeof attributes === 'string' ? JSON.parse(attributes) : attributes;
  } catch (e) {
    parsedAttrs = {};
  }

  if (!parsedAttrs || Object.keys(parsedAttrs).length === 0) {
    return null;
  }

  return (
    <div className="pt-3 border-t space-y-1.5" style={{ borderColor: palette.borders.subtle }}>
      <div className="flex items-center justify-between mb-2">
        <div className={`${typography.size.metadata} font-semibold text-text-muted uppercase`}>Attributes</div>
        <div className="flex gap-1">
          <button
            onClick={() => setViewMode('structured')}
            className={`px-2 py-0.5 text-xs rounded transition-colors ${
              viewMode === 'structured'
                ? 'bg-accent-primary-base/20 text-accent-primary-base'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            Structured
          </button>
          <button
            onClick={() => setViewMode('raw')}
            className={`px-2 py-0.5 text-xs rounded transition-colors ${
              viewMode === 'raw'
                ? 'bg-accent-primary-base/20 text-accent-primary-base'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            Raw
          </button>
        </div>
      </div>

      {viewMode === 'structured' ? (
        <JSONTreeViewer
          data={parsedAttrs}
          initialExpandDepth={1}
          maxHeight="300px"
          showControls={true}
        />
      ) : (
        <pre
          className={`${typography.size.xs} p-2 rounded overflow-auto`}
          style={{
            color: palette.support.codeText,
            backgroundColor: palette.neutrals.canvas,
            maxHeight: '300px'
          }}
        >
          {JSON.stringify(parsedAttrs, null, 2)}
        </pre>
      )}
    </div>
  );
};

/**
 * SpanTab - Displays span metadata and model information
 * Matches old GraphTab.js formatting exactly
 */
const SpanTab = ({ span, spanKind, drilldownApi = null }) => {
  if (!span) {
    throw new Error('SpanTab requires span prop');
  }

  logger.debug('SpanTab rendering:', { spanKind, spanId: span.span_id });

  const isV2Span = span.__v2Span === true;
  const isLLMSpan = spanKind === 'llm';
  const isToolSpan = spanKind === 'tool';
  const isEmbeddingSpan = spanKind === 'embedding';
  const isRerankerSpan = spanKind === 'reranker';

  // Extract tool attributes for TOOL spans
  let toolAttrs = { name: null, description: null, parameters: null };
  if (isToolSpan) {
    if (isV2Span) {
      toolAttrs = {
        name: span.tool_name || null,
        description: span.tool_description || null,
        parameters: null,
      };
    } else {
      try {
        toolAttrs = extractToolAttributes(span.attributes);
      } catch (e) {
        logger.error('Failed to extract tool attributes:', e);
      }
    }
  }

  // Extract embedding attributes for EMBEDDING spans
  let embeddingAttrs = { model_name: null, embeddings: [] };
  if (isEmbeddingSpan) {
    if (isV2Span) {
      embeddingAttrs = {
        model_name: span.model_name || null,
        embeddings: span.embedding_text ? [{ text: span.embedding_text }] : [],
      };
    } else {
      try {
        embeddingAttrs = extractEmbeddingAttributes(span.attributes);
      } catch (e) {
        logger.error('Failed to extract embedding attributes:', e);
      }
    }
  }

  // Extract reranker attributes for RERANKER spans
  let rerankerAttrs = { query: null, model_name: null, top_k: null, input_documents: [], output_documents: [] };
  if (isRerankerSpan) {
    if (isV2Span) {
      rerankerAttrs = {
        query: span.reranker_query || null,
        model_name: span.model_name || null,
        top_k: null,
        input_documents: [],
        output_documents: [],
      };
    } else {
      try {
        rerankerAttrs = extractRerankerAttributes(span.attributes);
      } catch (e) {
        logger.error('Failed to extract reranker attributes:', e);
      }
    }
  }

  const [drilldownDocuments, setDrilldownDocuments] = useState(null);
  const [drilldownDocumentsError, setDrilldownDocumentsError] = useState(null);
  useEffect(() => {
    setDrilldownDocuments(null);
    setDrilldownDocumentsError(null);
    if (!drilldownApi || !isRerankerSpan) return;
    let cancelled = false;
    drilldownApi.fetchDocuments()
      .then(data => { if (!cancelled) setDrilldownDocuments(data); })
      .catch(err => { if (!cancelled) setDrilldownDocumentsError(err?.message || 'Failed to load documents'); });
    return () => { cancelled = true; };
  }, [span.span_id, drilldownApi]); // eslint-disable-line

  if (isRerankerSpan && isV2Span && drilldownDocuments) {
    rerankerAttrs = {
      ...rerankerAttrs,
      input_documents: drilldownDocuments.reranker_input_documents || [],
      output_documents: drilldownDocuments.reranker_output_documents || [],
    };
  }

  return (
    <ErrorBoundary>
      <div className="space-y-3">
        {/* LLM Model Section - only for LLM spans */}
        {isLLMSpan && (span.llm_model_name || span.llm_provider || span.llm_system || span.llm_token_count_total > 0) && (
          <div className="space-y-1.5">
            <div className={`${typography.size.metadata} font-semibold uppercase mb-2`} style={{ color: palette.text.muted }}>Model</div>
            {span.llm_model_name && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Name</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{span.llm_model_name}</span>
              </div>
            )}
            {span.llm_provider && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Provider</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{span.llm_provider}</span>
              </div>
            )}
            {span.llm_system && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>System</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{span.llm_system}</span>
              </div>
            )}
            {span.prompt_tokens > 0 && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Prompt Tokens</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{span.prompt_tokens}</span>
              </div>
            )}
            {span.completion_tokens > 0 && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Completion Tokens</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{span.completion_tokens}</span>
              </div>
            )}
            {span.llm_token_count_total > 0 && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Total Tokens</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{span.llm_token_count_total}</span>
              </div>
            )}
          </div>
        )}

        {/* Tool Section - only for TOOL spans */}
        {isToolSpan && (toolAttrs.name || toolAttrs.description || toolAttrs.parameters) && (
          <div className="space-y-1.5">
            <div className={`${typography.size.metadata} font-semibold uppercase mb-2`} style={{ color: palette.text.muted }}>Tool</div>
            {toolAttrs.name && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Name</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{toolAttrs.name}</span>
              </div>
            )}
            {toolAttrs.description && (
              <div className="space-y-1">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Description</span>
                <div className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>
                  {toolAttrs.description}
                </div>
              </div>
            )}
            {toolAttrs.parameters && (
              <div className="space-y-1">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Parameters</span>
                <pre
                  className={`${typography.size.xs} p-2 rounded overflow-x-auto`}
                  style={{
                    color: palette.support.codeText,
                    backgroundColor: palette.neutrals.canvas,
                    maxHeight: '200px'
                  }}
                >
                  {JSON.stringify(toolAttrs.parameters, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Embedding Section - only for EMBEDDING spans */}
        {isEmbeddingSpan && (embeddingAttrs.model_name || embeddingAttrs.embeddings.length > 0) && (
          <div className="space-y-1.5">
            <div className={`${typography.size.metadata} font-semibold uppercase mb-2`} style={{ color: palette.text.muted }}>
              Embedding{embeddingAttrs.model_name ? `: ${embeddingAttrs.model_name}` : ''}
            </div>
            {embeddingAttrs.embeddings.length > 0 && (
              <div className="space-y-2">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>
                  Embedded Texts ({embeddingAttrs.embeddings.length})
                </span>
                {embeddingAttrs.embeddings.map((embedding, idx) => (
                  <div key={idx} className="space-y-1">
                    <div className={`${typography.size.xs} font-mono`} style={{ color: palette.text.muted }}>
                      Text {idx + 1}
                    </div>
                    <div
                      className={`${typography.size.xs} p-2 rounded`}
                      style={{
                        color: palette.support.codeText,
                        backgroundColor: palette.neutrals.canvas,
                        maxHeight: '150px',
                        overflowY: 'auto'
                      }}
                    >
                      {embedding.text}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reranker drilldown error */}
        {isRerankerSpan && isV2Span && drilldownDocumentsError && (
          <div className="p-3 text-sm rounded border" style={{ color: palette.status.danger, borderColor: withOpacity(palette.status.danger, 25), backgroundColor: withOpacity(palette.status.danger, 6) }}>
            Failed to load reranker documents: {drilldownDocumentsError}
          </div>
        )}

        {/* Reranker Section - only for RERANKER spans */}
        {isRerankerSpan && (rerankerAttrs.query || rerankerAttrs.model_name || rerankerAttrs.top_k || rerankerAttrs.input_documents.length > 0 || rerankerAttrs.output_documents.length > 0) && (
          <div className="space-y-1.5">
            <div className={`${typography.size.metadata} font-semibold uppercase mb-2`} style={{ color: palette.text.muted }}>Reranker</div>
            {rerankerAttrs.query && (
              <div className="space-y-1">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Query</span>
                <div className={`${typography.size.xs}`} style={{ color: palette.accents.primary.base }}>
                  {rerankerAttrs.query}
                </div>
              </div>
            )}
            {rerankerAttrs.model_name && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Model</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{rerankerAttrs.model_name}</span>
              </div>
            )}
            {rerankerAttrs.top_k && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Top K</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{rerankerAttrs.top_k}</span>
              </div>
            )}
            {rerankerAttrs.input_documents.length > 0 && (
              <div className="space-y-2 pt-2">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>
                  Input Documents ({rerankerAttrs.input_documents.length})
                </span>
                {rerankerAttrs.input_documents.map((doc, idx) => (
                  <div
                    key={idx}
                    className={`${typography.size.xs} p-2 rounded`}
                    style={{
                      backgroundColor: palette.neutrals.canvas,
                      borderLeft: '3px solid',
                      borderColor: palette.accents.primary.base
                    }}
                  >
                    {doc.id && (
                      <div className="font-mono mb-1" style={{ color: palette.text.muted }}>ID: {doc.id}</div>
                    )}
                    {doc.content && (
                      <div style={{ color: palette.accents.primary.base, maxHeight: '100px', overflowY: 'auto' }}>
                        {doc.content}
                      </div>
                    )}
                    {doc.score !== undefined && (
                      <div className="font-mono mt-1" style={{ color: palette.accents.warm.base }}>Score: {doc.score}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {isV2Span && drilldownDocuments?.reranker_input_documents_truncated && (
              <div className="mt-1 px-2 py-1 rounded text-xs italic" style={{ backgroundColor: palette.neutrals.canvas, color: palette.text.muted }}>
                Input document list exceeds the display limit and was omitted.
              </div>
            )}
            {rerankerAttrs.output_documents.length > 0 && (
              <div className="space-y-2 pt-2">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>
                  Output Documents ({rerankerAttrs.output_documents.length})
                </span>
                {rerankerAttrs.output_documents.map((doc, idx) => (
                  <div
                    key={idx}
                    className={`${typography.size.xs} p-2 rounded`}
                    style={{
                      backgroundColor: palette.neutrals.canvas,
                      borderLeft: '3px solid',
                      borderColor: palette.accents.warm.base
                    }}
                  >
                    {doc.id && (
                      <div className="font-mono mb-1" style={{ color: palette.text.muted }}>ID: {doc.id}</div>
                    )}
                    {doc.content && (
                      <div style={{ color: palette.accents.primary.base, maxHeight: '100px', overflowY: 'auto' }}>
                        {doc.content}
                      </div>
                    )}
                    {doc.score !== undefined && (
                      <div className="font-mono mt-1" style={{ color: palette.accents.warm.base }}>Score: {doc.score}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {isV2Span && drilldownDocuments?.reranker_output_documents_truncated && (
              <div className="mt-1 px-2 py-1 rounded text-xs italic" style={{ backgroundColor: palette.neutrals.canvas, color: palette.text.muted }}>
                Output document list exceeds the display limit and was omitted.
              </div>
            )}
          </div>
        )}

        {/* Span Attributes Section */}
        <div className={`flex flex-col ${(isLLMSpan && (span.llm_model_name || span.llm_provider || span.llm_system || span.llm_token_count_total > 0)) || (isToolSpan && (toolAttrs.name || toolAttrs.description || toolAttrs.parameters)) || (isEmbeddingSpan && (embeddingAttrs.model_name || embeddingAttrs.embeddings.length > 0)) || (isRerankerSpan && (rerankerAttrs.query || rerankerAttrs.model_name || rerankerAttrs.top_k || rerankerAttrs.input_documents.length > 0 || rerankerAttrs.output_documents.length > 0)) ? 'pt-3 border-t' : ''}`} style={{ borderColor: palette.borders.subtle }}>
          <div className={`${typography.size.metadata} font-semibold uppercase mb-2`} style={{ color: palette.text.muted }}>Span</div>
          <SpanAttributesSection span={span} />
        </div>

        {/* Metrics Section - Quality scores and operational metrics */}
        {span.metrics && Object.keys(span.metrics).length > 0 && (
          <div className="pt-3 border-t space-y-1.5" style={{ borderColor: palette.borders.subtle }}>
            <div className={`${typography.size.metadata} font-semibold uppercase mb-2`} style={{ color: palette.text.muted }}>Metrics</div>

            {/* Operational Metrics */}
            {span.metrics.latency_ms !== undefined && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Latency</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{span.metrics.latency_ms}ms</span>
              </div>
            )}
            {span.metrics.has_error !== undefined && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Has Error</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: span.metrics.has_error ? palette.status.danger : palette.accents.primary.base }}>
                  {span.metrics.has_error ? 'Yes' : 'No'}
                </span>
              </div>
            )}
            {span.metrics.tool_name && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Tool Name</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{span.metrics.tool_name}</span>
              </div>
            )}
            {span.metrics.tool_success !== undefined && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Tool Success</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: span.metrics.tool_success ? palette.status.success : palette.status.danger }}>
                  {span.metrics.tool_success ? 'Yes' : 'No'}
                </span>
              </div>
            )}

            {/* Quality Scores - Phase 2 (Single-span) */}
            {span.metrics.answer_relevance !== undefined && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Answer Relevance</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.warm.base }}>{span.metrics.answer_relevance}</span>
              </div>
            )}
            {span.metrics.context_relevance !== undefined && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Context Relevance</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.warm.base }}>{span.metrics.context_relevance}</span>
              </div>
            )}

            {/* Quality Scores - Phase 3 (Multi-span) */}
            {span.metrics.faithfulness !== undefined && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Faithfulness</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.warm.base }}>{span.metrics.faithfulness}</span>
              </div>
            )}
            {span.metrics.tool_result_usage !== undefined && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Tool Result Usage</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.warm.base }}>{span.metrics.tool_result_usage}</span>
              </div>
            )}
            {span.metrics.tool_selection_accuracy !== undefined && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Tool Selection Accuracy</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.warm.base }}>{span.metrics.tool_selection_accuracy}</span>
              </div>
            )}

            {/* Cosine Similarity - Phase 1 */}
            {span.metrics.top_k_cosine_similarity !== undefined && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Top-K Cosine Similarity</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.warm.base }}>{span.metrics.top_k_cosine_similarity}</span>
              </div>
            )}

            {/* Evaluation Metadata */}
            {span.metrics.evaluation_model && (
              <div className="flex justify-between items-center">
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Evaluation Model</span>
                <span className={`${typography.size.xs} font-mono`} style={{ color: palette.text.muted }}>{span.metrics.evaluation_model}</span>
              </div>
            )}

            {/* Custom Evaluations */}
            {span.metrics.custom_evals && span.metrics.custom_evals.length > 0 && (
              <>
                {span.metrics.custom_evals.map((evalResult, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>
                      {evalResult.metric_name}
                    </span>
                    {evalResult.error ? (
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.status.danger }}>Error</span>
                    ) : (
                      <span className={`${typography.size.xs} font-mono`} style={{ color: evalResult.passed ? palette.status.success : palette.status.warning }}>
                        {evalResult.score !== null ? (evalResult.score * 100).toFixed(1) + '%' : 'N/A'}
                        {evalResult.passed ? ' ✓' : ' ✗'}
                      </span>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* Tool Metrics - show if any tool-related data exists (including zero-call issues like Type-2 hallucinations) */}
            {(span.metrics.tool_call_count > 0 ||
              span.metrics.tool_hallucination_detected ||
              span.metrics.tool_promised_not_called ||
              span.metrics.tool_silent_failure_detected ||
              (span.metrics.tool_json_errors && span.metrics.tool_json_errors.length > 0) ||
              (span.metrics.tool_schema_errors && span.metrics.tool_schema_errors.length > 0)) && (
              <div className="space-y-1.5 pt-2 border-t mt-2" style={{ borderColor: palette.borders.subtle }}>
                <div className={`${typography.size.xs} font-semibold uppercase mb-1`} style={{ color: palette.text.muted }}>Tool Metrics</div>
                {span.metrics.tool_call_count > 0 && (
                  <div className="flex justify-between items-center">
                    <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Tool Calls</span>
                    <span className={`${typography.size.xs} font-mono`} style={{ color: palette.accents.primary.base }}>{span.metrics.tool_call_count}</span>
                  </div>
                )}

                {/* Type 1 Hallucination: Tool called but not in available tools */}
                {span.metrics.tool_hallucination_detected && (
                  <div className="space-y-0.5">
                    <div className="flex justify-between items-center">
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Hallucinated Tools</span>
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.status.danger }}>Yes</span>
                    </div>
                    {span.metrics.tool_hallucinated_names && span.metrics.tool_hallucinated_names.length > 0 && (
                      <div className={`${typography.size.xs} font-mono pl-2`} style={{ color: palette.status.danger }}>
                        {span.metrics.tool_hallucinated_names.join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Type 2 Hallucination: Tool promised but not called */}
                {span.metrics.tool_promised_not_called && (
                  <div className="space-y-0.5">
                    <div className="flex justify-between items-center">
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Promised But Not Called</span>
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.status.warning }}>Yes</span>
                    </div>
                    {span.metrics.tool_promised_names && span.metrics.tool_promised_names.length > 0 && (
                      <div className={`${typography.size.xs} font-mono pl-2`} style={{ color: palette.status.warning }}>
                        {span.metrics.tool_promised_names.join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Silent Failure */}
                {span.metrics.tool_silent_failure_detected && (
                  <div className="space-y-0.5">
                    <div className="flex justify-between items-center">
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Silent Failure</span>
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.status.danger }}>Yes</span>
                    </div>
                    {span.metrics.tool_silent_failure_reason && (
                      <div className={`${typography.size.xs} font-mono pl-2`} style={{ color: palette.status.danger }}>
                        {span.metrics.tool_silent_failure_reason}
                      </div>
                    )}
                  </div>
                )}

                {/* JSON Validation Errors */}
                {span.metrics.tool_json_errors && span.metrics.tool_json_errors.length > 0 && (
                  <div className="space-y-0.5">
                    <div className="flex justify-between items-center">
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>JSON Errors</span>
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.status.warning }}>{span.metrics.tool_json_errors.length}</span>
                    </div>
                    <div className={`${typography.size.xs} font-mono pl-2`} style={{ color: palette.status.warning }}>
                      {span.metrics.tool_json_errors.join(', ')}
                    </div>
                  </div>
                )}

                {/* Schema Validation Errors */}
                {span.metrics.tool_schema_errors && span.metrics.tool_schema_errors.length > 0 && (
                  <div className="space-y-0.5">
                    <div className="flex justify-between items-center">
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.support.attributeLabel }}>Schema Errors</span>
                      <span className={`${typography.size.xs} font-mono`} style={{ color: palette.status.warning }}>{span.metrics.tool_schema_errors.length}</span>
                    </div>
                    <div className={`${typography.size.xs} font-mono pl-2`} style={{ color: palette.status.warning }}>
                      {span.metrics.tool_schema_errors.join(', ')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Attributes Section - Structured and Raw views (at bottom) */}
        {span.attributes && Object.keys(typeof span.attributes === 'string' ? JSON.parse(span.attributes) : span.attributes).length > 0 && (
          <AttributesSection attributes={span.attributes} />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default SpanTab;
